import type { ReactNode } from "react";
import { useLocation, Navigate } from "react-router-dom";

import { useCurrentPlatformContext } from "@/contexts";
import { useScopedHref, useScopeRelativePathname } from "@/hooks";

export type CurrentPlatformGuardProps = {
  children: ReactNode;
};

export const CurrentPlatformGuard = ({ children }: CurrentPlatformGuardProps) => {
  const { platform, loading } = useCurrentPlatformContext();
  const location = useLocation();
  const pathname = useScopeRelativePathname();
  const scopedHref = useScopedHref();

  // The dashboard shell requires a current platform — DashboardHeader (and many
  // pages) call `useCurrentPlatform`, which throws on null. This guard is the
  // sole enforcer of that invariant before the shell mounts, so once platform
  // resolution has settled it must route EVERY "no current platform" state to
  // onboarding, not just the zero-available-platforms case.
  //
  // A user can settle with `platform === null` while still having a non-zero
  // available count — e.g. the cold-start `me/available` default resolves to
  // nothing (an available id that no longer maps to a live platform, or a
  // count/default backend mismatch). Gating this redirect on
  // `availablePlatformsCount === 0` let that state fall through to the header
  // and crash with "Current platform not found". Impersonation never hits this
  // because it lands on a BE-resolved pre-scoped URL (see docs/impersonation.md)
  // and skips cold-start resolution entirely.
  if (!loading && !platform && pathname !== "/onboarding") {
    return <Navigate to={scopedHref("/onboarding")} state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};
